import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewsSection = ({ product }) => {
  const [sortBy, setSortBy] = useState('newest');
  const [showAllReviews, setShowAllReviews] = useState(false);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={`${
          index < rating ? 'text-warning fill-current' : 'text-accent-300'
        }`}
      />
    ));
  };

  const reviews = product?.reviews || [
    {
      id: 1,
      rating: 5,
      title: "Perfect gift for my sister's birthday!",
      content: "She absolutely loved it! The quality exceeded my expectations and the packaging was beautiful. Definitely ordering again.",
      author: "Sarah M.",
      date: "2024-01-15",
      verified: true,
      helpful: 12,
      photos: ["/assets/images/review1.jpg"],
      relationship: "Sister",
      occasion: "Birthday"
    },
    {
      id: 2,
      rating: 5,
      title: "Exceeded expectations",
      content: "The recipient was thrilled! Great quality and arrived exactly as described. The personal touch made it extra special.",
      author: "Michael R.",
      date: "2024-01-10",
      verified: true,
      helpful: 8,
      photos: [],
      relationship: "Friend",
      occasion: "Congratulations"
    },
    {
      id: 3,
      rating: 4,
      title: "Beautiful and well-made",
      content: "Really impressed with the craftsmanship. My mom uses it every day and always gets compliments. Worth every penny.",
      author: "Jessica L.",
      date: "2024-01-05",
      verified: true,
      helpful: 15,
      photos: ["/assets/images/review2.jpg", "/assets/images/review3.jpg"],
      relationship: "Mother",
      occasion: "Mother's Day"
    }
  ];

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 2);

  const ratingBreakdown = {
    5: 78,
    4: 15,
    3: 5,
    2: 1,
    1: 1
  };

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="bg-white rounded-2xl border border-accent-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-accent-800">
            Customer Reviews
          </h2>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => console.log('Write review')}
          >
            Write Review
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Overall Rating */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
              <span className="text-4xl font-bold text-accent-800">
                {product?.rating || 4.8}
              </span>
              <div className="flex">
                {renderStars(Math.floor(product?.rating || 4.8))}
              </div>
            </div>
            <p className="text-accent-600 mb-4">
              Based on {product?.reviewCount || 127} reviews
            </p>
            <div className="flex items-center justify-center md:justify-start space-x-4 text-sm text-accent-600">
              <div className="flex items-center">
                <Icon name="ThumbsUp" size={16} className="mr-1" />
                <span>{product?.recommendationRate || 96}% recommend</span>
              </div>
              <div className="flex items-center">
                <Icon name="Award" size={16} className="mr-1" />
                <span>Verified purchases</span>
              </div>
            </div>
          </div>

          {/* Rating Breakdown */}
          <div className="space-y-2">
            {Object.entries(ratingBreakdown).reverse().map(([stars, percentage]) => (
              <div key={stars} className="flex items-center space-x-2">
                <span className="text-sm w-6">{stars}</span>
                <Icon name="Star" size={14} className="text-warning" />
                <div className="flex-1 h-2 bg-accent-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warning transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm text-accent-600 w-8">{percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filter and Sort */}
      <div className="flex items-center justify-between bg-accent-50 rounded-2xl p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-accent-800">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-accent-200 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="newest">Newest</option>
            <option value="helpful">Most Helpful</option>
            <option value="rating">Highest Rating</option>
          </select>
        </div>
        <div className="flex items-center space-x-2 text-sm text-accent-600">
          <Icon name="Filter" size={16} />
          <span>All reviews</span>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {displayedReviews.map((review) => (
          <div key={review.id} className="bg-white rounded-2xl border border-accent-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {review.author.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-accent-800">{review.author}</span>
                    {review.verified && (
                      <div className="flex items-center px-2 py-1 bg-success-50 rounded-full">
                        <Icon name="Check" size={12} className="text-success mr-1" />
                        <span className="text-xs font-medium text-success">Verified</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-accent-600">
                    <span>{review.date}</span>
                    <span>•</span>
                    <span>Gift for {review.relationship}</span>
                    <span>•</span>
                    <span>{review.occasion}</span>
                  </div>
                </div>
              </div>
              <div className="flex">
                {renderStars(review.rating)}
              </div>
            </div>

            <h4 className="font-medium text-accent-800 mb-2">{review.title}</h4>
            <p className="text-accent-600 leading-relaxed mb-4">{review.content}</p>

            {/* Review Photos */}
            {review.photos && review.photos.length > 0 && (
              <div className="flex space-x-2 mb-4">
                {review.photos.map((photo, index) => (
                  <div key={index} className="w-16 h-16 bg-accent-100 rounded-lg overflow-hidden">
                    <img
                      src={photo}
                      alt={`Review photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Review Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button className="flex items-center space-x-1 text-sm text-accent-600 hover:text-accent-800 transition-colors duration-200">
                  <Icon name="ThumbsUp" size={16} />
                  <span>Helpful ({review.helpful})</span>
                </button>
                <button className="flex items-center space-x-1 text-sm text-accent-600 hover:text-accent-800 transition-colors duration-200">
                  <Icon name="MessageCircle" size={16} />
                  <span>Reply</span>
                </button>
              </div>
              <button className="text-sm text-accent-600 hover:text-accent-800 transition-colors duration-200">
                <Icon name="Flag" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Show More Button */}
      {!showAllReviews && reviews.length > 2 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setShowAllReviews(true)}
            className="px-8"
          >
            Show All {reviews.length} Reviews
          </Button>
        </div>
      )}

      {/* Review Insights */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-6">
        <h3 className="font-heading text-lg font-semibold text-accent-800 mb-4">
          Review Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary mb-1">
              {product?.giftSuccessRate || 94}%
            </div>
            <p className="text-sm text-accent-600">Recipients loved it</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary mb-1">
              {product?.repeatBuyRate || 78}%
            </div>
            <p className="text-sm text-accent-600">Would buy again</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent-800 mb-1">
              {product?.avgDeliveryTime || 3}
            </div>
            <p className="text-sm text-accent-600">Average delivery days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsSection;